import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { GeoLink } from 'src/app/shared/types/geo';
import { Actor } from 'src/app/shared/user/actor';

import { PermissionType } from '../../map-data-services/mapWorkspace/permission';
import { User } from '../../user/user';

export interface GeoDataUpdateJobs {
    links?: GeoLink[];
    total?: number;
    items?: GeoDataUpdateJob[];
    eTag?: string;
}

export enum TaskStatus {
    NEW = 'New',
    IN_PROGRESS = 'In_Progress',
    COMPLETE = 'Complete'
}

export enum FormStatus {
    NEEDS_UPDATING = 'NeedsUpdating',
    UPDATED = 'Updated',
    NEW = 'New'
}
export interface GeoDataUpdateJob {
    uuid?: string;
    todoId?: string;
    name?: string;
    description?: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    dueDate?: string;
    createdUtc?: string;
    updatedUtc?: string;
    percentageComplete?: number;
    forms?: GeoDataUpdateForm[];
    workspaceIds?: string[];
    todo?: object;
    assignees?: GeoDataUpdateJobAssignee[];
    returnedJobs?: GeoReturnedDataUpdateJob[];
    links?: GeoLink[];
    id?: string;
    eTag?: string;
}

export interface GeoDataUpdateForm {
    formId?: string;
    templateSeriesId?: string;
    status?: FormStatus;
}

export interface GeoDataUpdateJobAssignee {
    type?: PermissionType;
    id?: string;
    tidUuid?: string;
}

export interface GeoReturnedDataUpdateJob {
    id?: string;
    userId?: string;
    returnedUtc?: string;
}

export interface GeoPostDataUpdateJobRequest {
    name: string;
    description?: string;
    forms?: GeoDataUpdateForm[];
    assignees?: GeoDataUpdateJobAssignee[];
    workspaceId: string;
}
export interface ToDoDTO {
    id: string;
    type: string;
    title: string;
    label: string;
    description: string;
    dueDate: Date;
    percentComplete: number;
    status: string;
    priority: string;
    projectId: string;
    createdOn: Date;
    createdBy: Partial<User>;
    modifiedOn: Date;
    modifiedBy: Partial<User>;
    assignees?: Actor[];
}
export class TaskFeature {
    formId?: string;
    featureId: string;
    status: FormStatus;
    templateSeriesId: string;
}

export class TaskPatchDTO {
    name?: string;
    description?: string;
    status?: string;
    assignees?: {
        newAssignees?: Partial<Actor>[];
        removeAssigneesIds?: string[];
    };
    forms?: {
        upsertForms?: TaskFeature[];
        removeFormsIds?: string[];
    };
}

export enum TaskPanelTabs {
    ACTIVE = 'ActiveJobs',
    CLOSED = 'CompletedJobs'
}

export enum TaskPriority {
    CRITICAL = 'CRITICAL',
    HIGH = 'HIGH',
    NORMAL = 'NORMAL',
    LOW = 'LOW'
}

export enum TaskCreationMode {
    NEW = 'NEW',
    NEW_FROM_SELECTION = 'NEW_FROM_SELECTION',
    EDIT = 'EDIT'
}

export enum TaskSortMode {
    DATE_CREATED = 'DATE_CREATED',
    DATE_MODIFIED = 'DATE_MODIFIED',
    NAME = 'NAME'
}

export class Task {
    id: string = null;
    features: TaskFeature[] = [];
    totalFeatures = 0;
    totalCollectedFeatures = 0;
    newFeatures = 0; // New forms collected after task created
    percentageComplete = 0;
    totalTaskedForms = 0;

    label: string = null;
    name: string = null;
    description: string = null;
    priority = TaskPriority.NORMAL;
    status = TaskStatus.NEW;
    dueDate: Date = null;
    assignees: Partial<Actor>[] = [];
    createdOn: Date = null;
    modifiedOn: Date = null;
    createdBy: string = null;
    etag: string = null;

    // Added properties
    isClosed = false;
    selected = false;
    visible = false;
    type: string = null;
    returnedJobs: {
        id?: string;
        userId?: string;
        returnedUtc?: string;
    }[] = [];
    completedUserIds: string[] = [];
    isTodoCompletedByUnassignedUser = false;
    unassignedCompletedUser: Actor[] = [];
    title: string = null;

    taskFeatures: Feature[] = []; // managed by map-container.component

    constructor() {}

    static fromDTO(dto: GeoDataUpdateJob): Task {
        const task = new Task();

        if (dto) {
            task.id = dto.id;
            task.percentageComplete = dto.percentageComplete;
            task.features = dto.forms.map(form => {
                const { templateSeriesId, status, formId } = form;
                return { templateSeriesId, status, formId, featureId: formId };
            });
            task.totalFeatures = task.features.length;
            task.totalCollectedFeatures = task.features.filter(feature => feature.status === FormStatus.UPDATED).length;
            task.returnedJobs = dto.returnedJobs || [];
            task.title = dto.name;
            task.description = dto.description;
            task.status = dto.status;
            task.isClosed = dto.status === TaskStatus.COMPLETE;
            task.priority = dto.priority;

            if (dto.assignees) {
                task.completedUserIds = task.returnedJobs.map(job => job.userId);
                task.assignees = dto.assignees.map(assignee => {
                    let tmpActor = Actor.fromDTO(assignee, assignee.type);
                    tmpActor.isTodoCompleted = task.completedUserIds.indexOf(assignee.id) > -1;
                    return tmpActor;
                });
            } else if (task.returnedJobs.length > 0) {
                task.isTodoCompletedByUnassignedUser = true;
            }
            task.newFeatures = task.features.filter(feature => feature.status === 'New').length;
            task.createdOn = new Date(dto.createdUtc);
            task.modifiedOn = new Date(dto.updatedUtc);
            task.etag = dto.eTag;
        }

        return task;
    }
}
